/**
 * List -> detail page transition for `apps/app`.
 *
 * ## Why a push/slide and not the shared-element "hero" morph
 *
 * This file replaces `heroTransition.ts`, which morphed the tapped product image
 * into the detail page's hero using the View Transitions API. That was built on
 * the belief that eBay / Chrono24 / Artsy expand the image. **They do not** —
 * checked against the real apps, all of them PUSH the detail page in from the
 * right while the list stays put. The morph was a nice effect for the wrong
 * product.
 *
 * The push also survives contact with this app in a way the morph could not:
 * a certificate may have NO image (the list shows a placeholder glyph), and
 * morphing a placeholder into a real photo reads as a glitch. A push doesn't
 * care what the row contained.
 *
 * ## Why CSS `animation` rather than `transition`
 *
 * Load-bearing. An `animation` plays from its keyframes and leaves the element
 * at its NATURAL position; a `transition` needs the start state to be applied,
 * committed, then changed. If a transition's start state lands but the change
 * never runs — a browser that throttles rAF in a background tab, a paint the
 * engine skips — the page is stuck translated off-screen. With an animation the
 * same failure degrades to "the page simply appears", which is exactly today's
 * behaviour and never a blank screen.
 *
 * That is not hypothetical here: the preview pane this app is developed in
 * reports `visibilityState: "hidden"` and does not run rAF-driven animation at
 * all, so the no-animation path is a state we actively hit.
 *
 * ## Mobile only
 *
 * A push is a phone idiom — it reads as a card stack you can pop back off. At
 * tablet width and up the layout is wide, the detail page is not a full-screen
 * takeover of the list, and sliding a wide page horizontally reads as jank
 * rather than navigation. Those widths get a plain navigate.
 */

/** Duration in ms. Long enough to read as motion, short enough not to gate the tap. */
export const PAGE_SLIDE_MS = 260

/** Router state flag set by the list, consumed by the detail page. */
export interface SlideNavState {
  /**
   * Set only when the navigation came from tapping a list row on a phone.
   *
   * Deliberately NOT "animate whenever the detail page mounts": the most common
   * way into this page is an NFC tap on a physical tag, which is a cold load
   * with no previous screen to push away from. Sliding in there animates the
   * page away from nothing.
   */
  slideIn?: boolean
  /**
   * This navigation is a BACK step — the destination is the page we came FROM,
   * so it should enter from the LEFT, reversing the push that opened the child.
   *
   * Set by `usePushPage`'s `goBack`, never by a link. Without it the child spent
   * its full exit sliding away to the right and the parent then simply APPEARED
   * — 260ms of motion followed by a hard cut, which reads as the whole page
   * flashing rather than as a stack unwinding.
   *
   * Distinct from `slideIn` rather than folded into it because the DIRECTION is
   * the whole point: `slideIn` alone means "enter from the right", which is
   * forward. A single flag cannot express both halves of a stack.
   */
  slideBack?: boolean
}

/** Honour the OS "reduce motion" setting — a full-width slide is exactly the kind of large-area movement it exists to suppress. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * `sx` fragment for the incoming page. Spread onto the detail page's root.
 *
 * Resting state is untransformed, so a page that never animates is correctly
 * placed rather than off-screen — see the note above.
 */
export type SlideDirection = 'left' | 'right'

export const slideInSx = (
  active: boolean,
  from: SlideDirection = 'right',
  /** Alternates so the keyframe NAME changes and the animation restarts without
      a `key` remount (remounting the page subtree is what broke the layout). */
  cycle: 'A' | 'B' = 'A',
) =>
  active
    ? {
        /*
         * Distinct keyframe NAMES per direction. Emotion keys its cache on the
         * serialised styles, and two `@keyframes` blocks sharing one name are a
         * collision waiting to happen — the second registration is ignored, so a
         * left-entering page would silently animate from the right.
         */
        [`@keyframes veraPageSlideIn${from === 'left' ? 'Left' : 'Right'}${cycle}`]: {
          from: { transform: `translate3d(${from === 'left' ? '-100%' : '100%'}, 0, 0)` },
          to: { transform: 'translate3d(0, 0, 0)' },
        },
        /*
         * `forwards`, NOT `both`, and the difference is a failure mode rather than
         * a nicety.
         *
         * `both` includes BACKWARDS fill, which applies the FROM keyframe —
         * translateX(±100%), i.e. fully off-screen — from the moment the style is
         * attached until the animation actually starts ticking. Anywhere animations
         * do not run, that is not "no animation", it is a page parked off-screen
         * permanently. Measured in the dev preview pane, which does not tick CSS
         * animations (it reports visibilityState: hidden): the wrapper sat at
         * translateX(375) well past the 260ms duration.
         *
         * With `forwards` the element rests at its natural position until the
         * animation runs, so the worst case degrades to no animation instead of no
         * page. The cost is at most one frame showing the final position before the
         * first keyframe lands, which is invisible next to the alternative.
         *
         * `slideOutSx` keeps `both` deliberately — it is the reverse case, where
         * the element must HOLD its final off-screen frame until the route
         * changes, and it is driven by a caller that waits for it.
         */
        animation: `veraPageSlideIn${from === 'left' ? 'Left' : 'Right'}${cycle} ${PAGE_SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`,
        // The slide would otherwise paint a sliver of horizontal overflow while
        // the page is still partly off-screen.
        willChange: 'transform',
      }
    : {}

/**
 * `sx` fragment for the page LEAVING to the right — the reverse of `slideInSx`.
 *
 * Back has to be animated differently from forward. On the way in, the router
 * mounts the page and the animation plays on a live element. On the way back,
 * `navigate()` unmounts it on the same tick, so there is nothing left to
 * animate — exactly the failure the bulk-actions bar had. So the caller holds
 * the page mounted, plays this, and navigates when it finishes.
 *
 * `fill: both` matters here specifically: without it the page snaps back to its
 * resting position on the last frame and flashes before the route changes.
 */
export const slideOutSx = (active: boolean, to: SlideDirection = 'right') =>
  active
    ? {
        [`@keyframes veraPageSlideOut${to === 'left' ? 'Left' : 'Right'}`]: {
          from: { transform: 'translate3d(0, 0, 0)' },
          to: { transform: `translate3d(${to === 'left' ? '-100%' : '100%'}, 0, 0)` },
        },
        animation: `veraPageSlideOut${to === 'left' ? 'Left' : 'Right'} ${PAGE_SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both`,
        willChange: 'transform',
      }
    : {}

/**
 * Which way a TAB switch travels, from the tab bar's own left-to-right order.
 *
 * Derived from the index delta rather than assigned per tab, and that is the
 * whole point: the middle tab is genuinely bidirectional — Activity enters from
 * the right when you came from Certificates and from the left when you came from
 * Profile — so any per-tab constant would be wrong half the time. Moving to a tab
 * on the RIGHT brings its page in from the right; the tab bar becomes a map of
 * where the pages are rather than three unrelated destinations.
 */
export const tabSlideDirection = (fromIndex: number, toIndex: number): SlideDirection =>
  toIndex > fromIndex ? 'right' : 'left'

/*
 * There was a `riseInSx` / `riseOutSx` pair here — a page that rose from the
 * bottom — on the argument that a push and a modal are different gestures.
 * SharePage was its only caller and moved to the horizontal push, so both were
 * deleted rather than kept "for the sheets": the real bottom sheets are
 * `DrawerPopup`, which brings its own transition and never used these. If a
 * genuine full-page modal ever wants the rise, it is `slideInSx` with a Y
 * translate — cheaper to rewrite than to carry an export nothing calls.
 */

/**
 * The width at which the push stops being the right idiom.
 *
 * Note this is NOT the app's `md` (720) — the push stays correct on a large
 * phone and a portrait tablet, which are still one-handed, single-column
 * surfaces. 900 is where the layout genuinely stops being a card stack.
 *
 * It used to have a second job: a `shouldFade()` desktop test was written as its
 * exact complement, so every viewport got one transition and none got two. That
 * fade is gone (see the block at the end of this file), so the constant now has
 * only the mobile test to serve. It stays a named export rather than being
 * folded back into `shouldSlide` because the certificate list reads it directly.
 */
export const SLIDE_MAX_WIDTH = 900

/**
 * Should this navigation animate at all?
 *
 * Same test the list uses when it sets the `slideIn` flag, exported so the
 * forward and back directions cannot drift apart — a phone-width viewport and
 * no reduced-motion preference.
 */
export const shouldSlide = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < SLIDE_MAX_WIDTH && !prefersReducedMotion()

/**
 * The DESKTOP push, prototyped on the certificate <-> sub-view pair (VERA-315).
 *
 * ## Why a desktop transition at all, given the note below says one was deleted
 *
 * The deleted one was a route-level opacity ramp from a 0.42 floor, armed on ANY
 * navigation at >=900px, over whatever happened to be underneath. This is a
 * different thing on all three counts: it is a directional PUSH, it is armed only
 * on the two routes that already have a stack relationship, and it moves a short
 * distance rather than washing the whole page.
 *
 * What it buys is the thing the mobile push buys and the platform has no need
 * for: `apps/platform` is a console where a route change is a TOOL change, so an
 * instant swap is honest there. This app is a STACK — list -> certificate ->
 * Provenance — and it already asserts that on a phone. A stack that animates on
 * one device and teleports on another tells two stories about one hierarchy.
 *
 * ## Why it is SHORT, and why distance is the parameter that matters
 *
 * 24px, not the phone's full width. A full-width slide at 1280 is a lot of motion
 * for a navigation a pointer made deliberately, which is the objection that
 * grounded the mobile-only rule in `usePushPage` — and it is an objection to the
 * DISTANCE, not to the existence of a transition. At 24px the page arrives from
 * the correct side without the viewport appearing to travel.
 *
 * 180ms rather than the phone's 260: a shorter distance covered at the same
 * perceived speed, and short enough that it never delays a pointer user who
 * already knows where they are going.
 *
 * ## What it must NOT collide with
 *
 * `ContentFade` (120ms) is tied to CONTENT ARRIVAL over a footprint skeleton —
 * the platform's mechanism, and the reason the old route fade was wrong. This is
 * a transform, so the two animate different properties on different elements and
 * cannot fight; running an opacity ramp here as well is what would re-create the
 * double-animation this file warns about. Keep it a transform.
 */
export const DESKTOP_PUSH_PX = 24
export const DESKTOP_PUSH_MS = 180

/** The desktop counterpart of `shouldSlide` — same reduced-motion respect. */
export const shouldPushDesktop = (): boolean =>
  typeof window !== 'undefined' &&
  window.innerWidth >= SLIDE_MAX_WIDTH &&
  !prefersReducedMotion()

/**
 * Entrance for the desktop push. Same shape as `slideInSx` — including the
 * `forwards` fill, for the same reason: anywhere animations do not tick, the
 * element must rest at its natural position rather than parked off-axis.
 */
export const pushInSx = (
  active: boolean,
  from: SlideDirection = 'right',
  cycle: 'A' | 'B' = 'A',
) =>
  active
    ? {
        /*
         * TRANSFORM ONLY — no opacity, and this is the constraint that keeps the
         * prototype from repeating the deleted fade's mistake.
         *
         * This element renders INSIDE `ContentFade`, which is already ramping
         * opacity on an ancestor for 120ms. Two nested opacity animations
         * MULTIPLY, so the page would arrive dimmer than either intended and over
         * whichever duration finished last. The directional cue is the whole job
         * here; the fade is `ContentFade`'s job, and it already does it over a
         * footprint the skeleton reserved.
         */
        [`@keyframes veraDeskPushIn${from === 'left' ? 'Left' : 'Right'}${cycle}`]: {
          from: {
            transform: `translate3d(${from === 'left' ? '-' : ''}${DESKTOP_PUSH_PX}px, 0, 0)`,
          },
          to: { transform: 'translate3d(0, 0, 0)' },
        },
        animation: `veraDeskPushIn${from === 'left' ? 'Left' : 'Right'}${cycle} ${DESKTOP_PUSH_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`,
        willChange: 'transform',
      }
    : {}

/*
 * THE DESKTOP PAGE FADE WAS DELETED HERE, and what replaced it is the point.
 *
 * There used to be `PAGE_FADE_MS` (200), `FADE_FLOOR` (0.42), `shouldFade()` and
 * `fadeInSx()` — a route-change opacity ramp armed by `Default.tsx` on any
 * user-driven navigation at >=900px. It worked, in the sense that it ran: measured
 * in the preview pane, `veraPageFadeInA` reached `playState: 'finished'` on a real
 * click. It was still the wrong device, for a reason that only shows up when you
 * go and look at what `apps/platform` does.
 *
 * ## What the platform actually does on a page change
 *
 * Nothing, at the route level. `apps/platform/src/layouts/Layout/Layout.tsx` renders
 * `<Outlet />` inside a plain `<Box sx={{ p: 6 }}>` — no Fade, no keyframes, no
 * transition group, and the platform has no animation library in its dependencies.
 * A platform route change is an instantaneous swap.
 *
 * Its transition is the LOADING HANDOFF instead, and it has two halves:
 *
 *   1. a footprint-parity skeleton, gated by `useDeferredLoading` — 180ms grace so a
 *      fast response never blinks, and a 320ms floor so anything shown reads as
 *      deliberate (`apps/platform/src/hooks/useDeferredLoading.ts`);
 *   2. `<Fade in timeout={120}>` wrapping the loaded page's OWN root — see
 *      `SupportRequestPage.tsx` and `WhatsNewAnnouncementPage.tsx`, whose comments
 *      say it explicitly: "the whole page is what gets replaced, so the whole page
 *      is what fades".
 *
 * So the platform's fade is tied to CONTENT ARRIVAL, over a footprint a skeleton
 * already reserved. The one deleted here was tied to the ROUTE CHANGE, from a 0.42
 * floor, over whatever happened to be underneath. Those are different mechanisms
 * that happen to share the word "fade", and running both would have double-animated
 * every page: the wrapper fading in, then the skeleton fading to content inside it.
 *
 * ## Why this was the actual defect
 *
 * Measured at 1280 before the change, the app's skeletons were footprint-exact and
 * lasted 60.0ms (certificates -> a certificate) and 220.5ms (certificate ->
 * Provenance), with the Scans map showing none at all. A placeholder that appears
 * and vanishes inside 60ms is a flicker, and a 200ms wash from 0.42 was papering
 * over it rather than fixing it — which is why the page change still read as having
 * no transition at all.
 *
 * `useDeferredLoading` (ported to `hooks/useDeferredLoading.ts`) plus a 120ms
 * content Fade is the platform's answer to precisely this symptom, so the app now
 * uses the same one instead of a second invention.
 *
 * ## Mobile is untouched
 *
 * `slideInSx` / `slideOutSx` / `shouldSlide` above are unchanged — the phone push is
 * a different idiom for a different surface and was never what was broken. The fade
 * was desktop-only (`>= SLIDE_MAX_WIDTH`), so removing it cannot reach <900px.
 */


/**
 * How long an in-SHEET step takes. Shorter than PAGE_SLIDE_MS (260) because the
 * travel is shorter: a page slides its full width, a sheet step moves a fraction
 * of a ~343px sheet, and matching the page duration over that distance reads
 * sluggish rather than considered.
 */
export const SHEET_STEP_MS = 200

/**
 * A step swap WITHIN one bottom sheet — the second half of working-conventions §52.
 *
 * §52 forbids a third stacked drawer: when a level-2 sheet needs to show something
 * further, that something is a STEP inside the sheet already open. This is what makes
 * such a step read as advancing rather than as a new surface cutting in.
 *
 * Direction follows page navigation exactly — forward enters from the RIGHT, back from
 * the LEFT — so "deeper" moves the same way whether the thing going deeper is a page or
 * a sheet, and the gesture is learned once (`slideInSx` above is the page equivalent).
 *
 * 16%, not 100%: the sheet is only ~343px wide, so a full-width translate would throw
 * the content clean off screen and back for what is often a change of a few rows. 16%
 * reads as "this moved" without the content ever leaving the sheet.
 *
 * Extracted from SortDrawer (§36 — it was the first hand-roll, ScanTag's "Where to scan"
 * step was the second). It lives beside the page transitions rather than in a component
 * because the direction convention is the shared thing: the two must not drift apart.
 *
 * Pass `null` when the step did not change, so a re-render does not replay the animation.
 */
export const sheetStepSx = (enterFrom: SlideDirection | null) =>
  enterFrom
    ? {
        animation: `veraSheetStep${enterFrom === 'right' ? 'Fwd' : 'Back'} ${SHEET_STEP_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both`,
        '@keyframes veraSheetStepFwd': {
          from: {transform: 'translateX(16%)', opacity: 0},
          to: {transform: 'translateX(0)', opacity: 1},
        },
        '@keyframes veraSheetStepBack': {
          from: {transform: 'translateX(-16%)', opacity: 0},
          to: {transform: 'translateX(0)', opacity: 1},
        },
        '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
      }
    : undefined
